import { useEffect, useRef } from 'react'
import Show from './show'
import './grid.css'

import { isObjectPerson } from '../tools/typeGuards'

interface GridProps {
  infiniteFunc: () => void
  shows: (
    | TMDB.Movie
    | TMDB.TvShow
    | Iziplay.Downloaded
    | TMDB.MovieDiscoverResult
    | TMDB.TVDiscoverResult
    | TMDB.Person
  )[]
}

function Grid(props: GridProps) {
  const { shows, infiniteFunc } = props
  const gridRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // The observer outlives renders; always call the newest callback.
  const loadMore = useRef(infiniteFunc)
  useEffect(() => {
    loadMore.current = infiniteFunc
  })

  /*
   * A sentinel after the last card asks for more when it comes into view.
   * Re-armed whenever the list grows, so a page too short to fill the viewport
   * keeps loading until the sentinel is pushed out of sight. Not armed while
   * the list is empty: the first page is the caller's to fetch.
   */
  useEffect(() => {
    const root = gridRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel || shows.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) loadMore.current()
      },
      { root, rootMargin: '0px 0px 400px 0px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [shows.length])

  const mappedShows = shows.map((show, i) => {
    if (!show) return null

    if (isObjectPerson(show) && show.known_for && show.media_type === 'person') {
      const knownForShow = show.known_for
      return knownForShow.map((know: any) => <Show key={know.id} details={know} />)
    }
    return <Show key={i} details={show} />
  })
  return (
    <div className="grid scrollable" ref={gridRef}>
      {mappedShows}
      <div className="grid__sentinel" ref={sentinelRef} aria-hidden />
    </div>
  )
}

export default Grid
